import axios from 'axios'
import { useAuthStore } from '../store/authStore'
import type { ExitBacktestReport } from '../types/exitBacktest'
import type { ShadowOutcomesReport } from '../types/shadowOutcomes'
import type { Phase2ModelPerformance } from '../types/phase2Model'
import type { Phase2Comparison } from '../types/phase2Comparison'
import type { Phase2Trial } from '../types/phase2Trial'
import type { ShadowFiltersReport } from '../types/shadowFilters'
import type { ScheduledTasksReport } from '../types/scheduledTasks'
import type { OutcomeAuditReport } from '../types/outcomeAudit'
import type { EnginePauseReport } from '../types/enginePause'
import type { TipGoal, TipGoalCreate } from '../types/tipGoals'
import type { Poll, PollCreate } from '../types/polls'
import type { CapabilityReport } from '../types/capabilities'
import type {
  DiagnosisConversationDetail,
  DiagnosisConversations,
  DiagnosisTurn,
  DiagnosticsFacts,
  EngineEvents,
} from '../types/adminDiagnostics'
import type {
  AlertTrigger,
  ClosedSignalEvent,
  NewsHeadline,
  PaperAccountSummary,
  PaperActivityEvent,
  PaperEquityCurve,
  PaperPosition,
  PaperRegime,
  TradingSignal,
  ScanResult,
  RiskStatus,
  PortfolioHeat,
  RetrainResult,
  ClipsSource,
  Content,
  ContentExport,
  CertificateCheck,
  Course,
  CourseCertificate,
  CourseDiscussion,
  CourseEnrollment,
  CourseReportItem,
  CourseReportReason,
  CourseReview,
  CourseReviewsPage,
  CourseModule,
  CourseQuestion,
  Lesson,
  LessonProgress,
  LessonQuestion,
  QuizQuestion,
  QuizResult,
  QuizView,
  HashtagSuggestions,
  CreatorPost,
  PostImageOptions,
  PostsSummary,
  NewsletterStatus,
  NewsletterSubscribers,
  AddSubscribersResult,
  NewsletterCampaign,
  NewsletterCampaignBody,
  DripPiece,
  HouseAccount,
  LibraryNote,
  AudienceInsights,
  Workflow,
  WorkflowRun,
  ContentMoment,
  TopicIdea,
  EnginePause,
  EngineVersionRecord,
  CoachResult,
  ConversionStats,
  LibraryAnswer,
  UnlockedDripPiece,
  DailyRecapResult,
  RecapPlatform,
  RecapAutoDraftPlatform,
  RecapAutoDraftSettings,
  AnalyticsOverview,
  PlatformDistribution,
  SignalPerformance,
  Post,
  Comment,
  UserProfile,
  Notification,
  SubscriptionTier,
  Subscription,
  Payout,
  Tip,
  User,
  ScheduledContent,
  ScheduledContentType,
  ScheduledContentStatus,
  ContentFormat,
  QuickRepurposeResult,
  TradeRecord,
  TradeDirection,
  PerformancePeriod,
  TrackRecordSummary,
  PaymentMethod,
  PublicProfileResponse,
  ConsentType,
  ConsentStatus,
  ConsentRecord,
  DataRequest,
  DisclaimerResponse,
  CreatorVettingResponse,
  CreatorVettingSubmission,
  CreatorVettingReview,
  ABTest,
  ABTestCreateRequest,
  ABTestStatus,
  ABTestReport,
  LiveSession,
  LiveSessionCreateRequest,
  LiveJoinToken,
  LiveChatMessage,
  CreatorProfile,
  DomainVerificationToken,
  DomainVerificationResult,
  SignalPerformanceSummary,
  TickerTape,
  SparklinesResponse,
  CandleSeries,
  ChartTimeframe,
  AssistantAnswer,
  AssistantQuestion,
  ScanStatus,
  SignalTrackRecordSummary,
  CreatorRecord,
  FeeCalculatorRecord,
  ExcludedSignal,
  Phase2DeciderChange,
  PublicCall,
  CallOptions,
  LockCallRequest,
  LockedCall,
  DigestListing,
  CallCheck,
  EngineActivity,
  EquityCurvePoint,
  TradingViewSetup,
} from '../types'
import { toTickerHeadlines } from '../utils/newsHeadlines'

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000'
/** The backend's base URL, e.g. for showing /api/v1 to developers. */
export const API_BASE_URL = API_URL

// Endpoints that should never trigger an access-token refresh on 401: either
// they are part of the login/registration flow itself, or (for /refresh) a
// 401 there means the refresh token is no longer valid.
const NO_REFRESH_PATHS = [
  '/api/auth/login',
  '/api/auth/register',
  '/api/auth/refresh',
  '/api/auth/2fa/confirm',
  '/api/auth/forgot-password',
  '/api/auth/reset-password',
]

const api = axios.create({
  baseURL: API_URL,
})

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().token
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// On a 401, try to use the refresh token to get a new access token and
// retry the original request once. Concurrent 401s while a refresh is in
// flight are queued and retried with the new token once it's available.
let isRefreshing = false
let failedQueue: Array<{ resolve: (token: string) => void; reject: (error: unknown) => void }> = []

const processQueue = (error: unknown, token: string | null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (token) {
      resolve(token)
    } else {
      reject(error)
    }
  })
  failedQueue = []
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    const isAuthFlowEndpoint = NO_REFRESH_PATHS.some((path) =>
      originalRequest?.url?.includes(path)
    )

    if (
      error.response?.status !== 401 ||
      !originalRequest ||
      isAuthFlowEndpoint ||
      originalRequest._retry
    ) {
      return Promise.reject(error)
    }

    const { refreshToken, user, setAuth, logout } = useAuthStore.getState()

    if (!refreshToken) {
      logout()
      return Promise.reject(error)
    }

    originalRequest._retry = true

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({
          resolve: (token: string) => {
            originalRequest.headers.Authorization = `Bearer ${token}`
            resolve(api.request(originalRequest))
          },
          reject,
        })
      })
    }

    isRefreshing = true

    try {
      const { data } = await api.post('/api/auth/refresh', { refresh_token: refreshToken })
      setAuth(data.access_token, user as User, data.refresh_token)
      processQueue(null, data.access_token)

      originalRequest.headers.Authorization = `Bearer ${data.access_token}`
      return api.request(originalRequest)
    } catch (refreshError) {
      processQueue(refreshError, null)
      logout()
      return Promise.reject(refreshError)
    } finally {
      isRefreshing = false
    }
  }
)

// Auth
export const authAPI = {
  login: async (username: string, password: string) => {
    const formData = new FormData()
    formData.append('username', username)
    formData.append('password', password)
    const response = await api.post('/api/auth/login', formData)
    return response.data
  },

  register: async (data: { email: string; username: string; password: string; full_name?: string; tos_accepted?: boolean }) => {
    const response = await api.post('/api/auth/register', data)
    return response.data
  },

  acceptTos: async () => {
    const response = await api.post('/api/auth/tos/accept')
    return response.data
  },

  confirmTwoFactor: async (challengeToken: string, code: string) => {
    const response = await api.post('/api/auth/2fa/confirm', {
      challenge_token: challengeToken,
      code,
    })
    return response.data
  },

  getProfile: async (token?: string) => {
    const response = await api.get(
      '/api/auth/me',
      token ? { headers: { Authorization: `Bearer ${token}` } } : undefined
    )
    return response.data
  },

  // The fee saved in the fee calculator (percent per side, fee only); null: none.
  getMyFee: async () => {
    const response = await api.get<{ fee_pct_per_side: number | null }>('/api/auth/me/fee')
    return response.data
  },

  // Save (or, with null, clear) the fee calculator's fee: 0-5% per side.
  saveMyFee: async (feePctPerSide: number | null) => {
    const response = await api.put<{ fee_pct_per_side: number | null }>('/api/auth/me/fee', {
      fee_pct_per_side: feePctPerSide,
    })
    return response.data
  },

  forgotPassword: async (email: string) => {
    const response = await api.post('/api/auth/forgot-password', { email })
    return response.data
  },

  resetPassword: async (token: string, new_password: string) => {
    const response = await api.post('/api/auth/reset-password', { token, new_password })
    return response.data
  },

  verifyEmail: async (token: string) => {
    const response = await api.get(`/api/auth/verify-email/${token}`)
    return response.data
  },
}

// Paper forward test — pretend money on real prices, isolated from the real
// record. Read-only by design: the account is driven only by the daily job.
export const paperAPI = {
  account: async () => {
    const response = await api.get<PaperAccountSummary>('/api/paper/account')
    return response.data
  },

  positions: async (params?: { status?: 'open' | 'closed'; limit?: number }) => {
    const response = await api.get<PaperPosition[]>('/api/paper/positions', { params })
    return response.data
  },

  equityCurve: async (limit = 365) => {
    const response = await api.get<PaperEquityCurve>('/api/paper/equity-curve', { params: { limit } })
    return response.data
  },

  activity: async (limit = 20) => {
    const response = await api.get<PaperActivityEvent[]>('/api/paper/activity', { params: { limit } })
    return response.data
  },

  regime: async () => {
    const response = await api.get<PaperRegime>('/api/paper/regime')
    return response.data
  },
}

// Trading Signals
export const signalsAPI = {
  /** Phase 2 model metadata: the newest retrain and the recent attempt log (read-only). */
  modelPerformance: async () => {
    const response = await api.get<Phase2ModelPerformance>('/api/signals/model-performance')
    return response.data
  },
  // Cancelled signals are excluded unless include_cancelled is set.
  list: async (params?: {
    skip?: number
    limit?: number
    status?: string
    symbol?: string
    include_cancelled?: boolean
    /** true: demo signals only; false: real signals only; omitted: both. */
    demo?: boolean
  }) => {
    const response = await api.get<TradingSignal[]>('/api/signals/', { params })
    return response.data
  },

  // Real signal closes, newest first, for the notification bell
  getClosedFeed: async (params?: { since?: string; limit?: number }) => {
    const response = await api.get<ClosedSignalEvent[]>('/api/signals/closed-feed', { params })
    return response.data
  },

  analyze: async (symbol: string, timeframe: string = '1h', mode: 'conservative' | 'growth' = 'conservative') => {
    const response = await api.post<TradingSignal>(`/api/signals/analyze/${symbol}`, null, {
      params: { timeframe, mode },
    })
    return response.data
  },

  scan: async (params?: { symbols?: string[]; timeframes?: string[]; mode?: 'conservative' | 'growth' }) => {
    const response = await api.post<ScanResult>('/api/signals/scan', params ?? {})
    return response.data
  },

  riskStatus: async () => {
    const response = await api.get<RiskStatus>('/api/signals/risk-status')
    return response.data
  },

  portfolioHeat: async () => {
    const response = await api.get<PortfolioHeat>('/api/signals/portfolio-heat')
    return response.data
  },

  scanStatus: async () => {
    const response = await api.get<ScanStatus>('/api/signals/scan-status')
    return response.data
  },

  // Cumulative P&L over closed real signals, scoped like the track-record
  // summary so the curve plots the trades its win rate counts. ~10 years is
  // "all time" for this product.
  // Gross: no fee is passed, so the API's net fields are null. A fee is
  // applied only in the fee calculator (feeCalculatorAPI).
  equityCurve: async (days = 3650) => {
    const response = await api.get<EquityCurvePoint[]>('/api/signals/equity-curve', { params: { days } })
    return response.data
  },

  // Recent scans and signal closes, newest first.
  engineActivity: async (limit = 20) => {
    const response = await api.get<EngineActivity>('/api/signals/engine-activity', { params: { limit } })
    return response.data
  },

  retrain: async () => {
    const response = await api.post<RetrainResult>('/api/signals/retrain', {})
    return response.data
  },

  performanceSummary: async (days: number = 90) => {
    const response = await api.get<SignalPerformanceSummary>('/api/signals/performance-summary', {
      params: { days },
    })
    return response.data
  },

  generatePerformanceContent: async (platform: string = 'twitter', days: number = 90) => {
    const response = await api.post<{ generated_text: string; stats_used: SignalPerformanceSummary; model: string }>(
      '/api/signals/performance-content',
      null,
      { params: { platform, days } },
    )
    return response.data
  },
}

// Content
/** A creator's workflows: what happens when a call locks or closes, or a post goes out. */
export const workflowsAPI = {
  list: async () => (await api.get<{ templates: Workflow[] }>('/api/workflows/templates')).data.templates,
  create: async (data: { name: string; trigger_type: string; actions: Workflow['actions'] }) =>
    (await api.post<{ id: number }>('/api/workflows/templates', data)).data,
  setActive: async (id: number, is_active: boolean) => {
    await api.patch(`/api/workflows/templates/${id}`, { is_active })
  },
  remove: async (id: number) => {
    await api.delete(`/api/workflows/templates/${id}`)
  },
  runs: async (id: number) =>
    (await api.get<{ executions: WorkflowRun[] }>(`/api/workflows/executions/${id}`, { params: { limit: 10 } })).data
      .executions,
}

/** A creator's audience: followers, subscribers, conversion, retention. */
export const audienceAPI = {
  insights: async () => (await api.get<AudienceInsights>('/api/creator-analytics/audience/insights')).data,
  conversion: async () => (await api.get<ConversionStats>('/api/creator-analytics/conversion-stats')).data,
}

/** The creator's notes, and questions answered from them. */
export const libraryAPI = {
  list: async () => (await api.get<{ total: number; documents: LibraryNote[] }>('/api/content-library/documents')).data,
  add: async (data: { title: string; content: string; document_type: string; related_symbol?: string }) =>
    (await api.post<{ id: number; processing_status: string }>('/api/content-library/upload', data)).data,
  remove: async (id: number) => {
    await api.delete(`/api/content-library/documents/${id}`)
  },
  ask: async (question: string) => (await api.post<LibraryAnswer>('/api/content-library/ask', { question })).data,
}

/** FinCreator's own posting accounts; switching them is admin only. */
export const houseAccountsAPI = {
  list: async () => (await api.get<HouseAccount[]>('/api/platforms/')).data,
  update: async (id: number, data: { is_active?: boolean; auto_publish?: boolean }) =>
    (await api.patch<HouseAccount>(`/api/platforms/${id}`, data)).data,
}

/** Drip content: pieces subscribers unlock by days subscribed. */
export const dripAPI = {
  schedule: async () => (await api.get<DripPiece[]>('/api/drip-content/schedule')).data,
  create: async (data: { title: string; body: string; unlock_day: number; tier_id?: number | null }) =>
    (await api.post<DripPiece>('/api/drip-content/', data)).data,
  remove: async (id: number) => {
    await api.delete(`/api/drip-content/${id}`)
  },
  /** For a subscriber: what they've unlocked from one creator. */
  unlocked: async (creatorId: number) =>
    (await api.get<UnlockedDripPiece[]>('/api/drip-content/unlocked', { params: { creator_id: creatorId } })).data,
}

/** A creator's newsletter: list, drafts, sending, results. */
export const newsletterAPI = {
  status: async () => (await api.get<NewsletterStatus>('/api/newsletters/status')).data,
  subscribers: async () => (await api.get<NewsletterSubscribers>('/api/newsletters/subscribers')).data,
  /** People who agreed to get it; `consent` is the creator saying they did. */
  addSubscribers: async (addresses: string, consent: boolean) =>
    (await api.post<AddSubscribersResult>('/api/newsletters/subscribers', { addresses, consent })).data,
  removeSubscriber: async (id: number) => {
    await api.delete(`/api/newsletters/subscribers/${id}`)
  },
  campaigns: async () => (await api.get<NewsletterCampaign[]>('/api/newsletters/campaigns')).data,
  campaign: async (id: number) => (await api.get<NewsletterCampaignBody>(`/api/newsletters/campaigns/${id}/body`)).data,
  create: async (data: { subject: string; html_body: string; text_body?: string }) =>
    (await api.post<NewsletterCampaign>('/api/newsletters/campaigns', data)).data,
  update: async (id: number, data: { subject?: string; html_body?: string; text_body?: string }) =>
    (await api.patch<NewsletterCampaignBody>(`/api/newsletters/campaigns/${id}`, data)).data,
  remove: async (id: number) => {
    await api.delete(`/api/newsletters/campaigns/${id}`)
  },
  /** The email as subscribers will get it (checked, with the footer). */
  preview: async (id: number) =>
    (await api.get<{ subject: string; html: string; text: string }>(`/api/newsletters/campaigns/${id}/preview`)).data,
  sendTest: async (id: number) => {
    await api.post(`/api/newsletters/campaigns/${id}/test`)
  },
  send: async (id: number) => (await api.post<NewsletterCampaign>(`/api/newsletters/campaigns/${id}/send`, {})).data,
  /** Public: does a creator's page take sign-ups, and sign up. */
  signupStatus: async (handle: string) =>
    (await api.get<{ handle: string | null; accepting: boolean }>(`/api/newsletters/signup/${encodeURIComponent(handle)}`)).data,
  signup: async (handle: string, email: string) =>
    (await api.post<{ detail: string }>(`/api/newsletters/signup/${encodeURIComponent(handle)}`, { email })).data,
}

export const creatorPostsAPI = {
  list: async (params?: { content_id?: number }) =>
    (await api.get<CreatorPost[]>('/api/creator-posts/', { params })).data,
  add: async (data: { url: string; content_id?: number }) =>
    (await api.post<CreatorPost>('/api/creator-posts/', data)).data,
  setCounts: async (id: number, counts: Partial<Pick<CreatorPost, 'views' | 'likes' | 'comments' | 'shares'>>) =>
    (await api.patch<CreatorPost>(`/api/creator-posts/${id}`, counts)).data,
  refresh: async (id: number) => (await api.post<CreatorPost>(`/api/creator-posts/${id}/refresh`)).data,
  /** How the posts are doing, per network and kind of post. */
  summary: async () => (await api.get<PostsSummary>('/api/creator-posts/summary')).data,
  remove: async (id: number) => {
    await api.delete(`/api/creator-posts/${id}`)
  },
}

export const contentAPI = {
  list: async (params?: { skip?: number; limit?: number; content_type?: string; status?: string }) => {
    const response = await api.get<Content[]>('/api/content/', { params })
    return response.data
  },
  /** The draft as the creator may post it themselves, and where to post it. */

  exportFor: async (id: number) => (await api.get<ContentExport>(`/api/content/${id}/export`)).data,
  /** The images a draft can have, and their sizes. */

  imageOptions: async (id: number) =>
    (await api.get<PostImageOptions>(`/api/content/${id}/images`)).data,
  /** One image for a draft, as a PNG blob (the request needs the login, so no plain <img src>). */

  image: async (id: number, kind: string, size: string) =>
    (await api.get<Blob>(`/api/content/${id}/images/${kind}.png`, { params: { size }, responseType: 'blob' })).data,

  create: async (data: {
    content_type: 'twitter' | 'instagram' | 'youtube' | 'tiktok' | 'linkedin'
    title?: string
    text: string
    hashtags?: string[]
    media_urls?: string[]
    description?: string
    source?: ClipsSource
  }) => {
    const response = await api.post<Content>('/api/content/', data)
    return response.data
  },

  generate: async (data: {
    content_type: string
    signal_id?: number
    /** One of the caller's own calls: the draft quotes its result, R and close reason as recorded. */
    call_id?: number
    topic?: string
    additional_context?: string
    /** What the creator said, in their own words: the post keeps their points and voice. */
    own_words?: string
    ai_model?: string
  }) => {
    const response = await api.post<Content>('/api/content/generate', data)
    return response.data
  },
  /** The outbound check, for an editor to show while the creator types. */

  check: async (data: { text: string; content_type?: string; recap_date?: string | null }) =>
    (await api.post<CoachResult>('/api/content/check', data)).data,
  /** Three other first lines for a draft, to pick from. */

  hooks: async (id: number) => (await api.post<{ hooks: string[] }>(`/api/content/${id}/hooks`)).data.hooks,
  /** Hashtags for a post, leaving out the ones it has and any that promise a result. */
  suggestHashtags: async (topic: string, platform: string) =>
    (await api.post<HashtagSuggestions>('/api/hashtag-research/suggest', { topic, platform })).data,
  /** Ideas for a post from a topic; round is how many times More ideas was pressed. */

  topicIdeas: async (round = 0) =>
    (await api.get<{ ideas: TopicIdea[] }>('/api/content/topic-ideas', { params: { round } })).data.ideas,
  /** What's worth posting about now, from the creator's own record. */

  moments: async () => (await api.get<{ moments: ContentMoment[] }>('/api/content/moments')).data.moments,

  // Drafts a recap of the current UTC day's real signal closes, dated on its
  // card. Draft only: nothing is published. With no closes yet it drafts nothing and
  // spends no quota.
  dailyRecap: async (platform: RecapPlatform = 'twitter') => {
    const response = await api.post<DailyRecapResult>('/api/content/daily-recap', null, {
      params: { platform },
    })
    return response.data
  },

  // Admin only: per-platform auto-drafting of the house daily recap.
  getRecapAutoDraft: async () => {
    const response = await api.get<RecapAutoDraftSettings>('/api/content/recap-autodraft')
    return response.data
  },

  saveRecapAutoDraft: async (platforms: RecapAutoDraftPlatform[]) => {
    const response = await api.put<RecapAutoDraftSettings>('/api/content/recap-autodraft', {
      platforms: platforms.map(({ platform, enabled, draft_time }) => ({
        platform,
        enabled,
        draft_time,
      })),
    })
    return response.data
  },

  update: async (id: number, data: Partial<Content>) => {
    const response = await api.patch<Content>(`/api/content/${id}`, data)
    return response.data
  },

  publish: async (id: number, platform_ids?: number[]) => {
    const response = await api.post(`/api/content/${id}/publish`, { platform_ids })
    return response.data
  },

  // Soft delete: the row is hidden everywhere but kept for publication history.
  delete: async (id: number) => {
    await api.delete(`/api/content/${id}`)
  },
}

// Content Repurposing
export const contentRepurposingAPI = {
  quickRepurpose: async (data: {
    source_text: string
    source_format: ContentFormat
    target_format: ContentFormat
  }) => {
    const response = await api.post<QuickRepurposeResult>(
      '/api/content-repurposing/quick-repurpose',
      data
    )
    return response.data
  },
}

// Analytics
export const analyticsAPI = {
  // demo=true scopes to demo signals; the default covers real signals only.
  overview: async (demo = false) => {
    const response = demo
      ? await api.get<AnalyticsOverview>('/api/analytics/overview', { params: { demo: true } })
      : await api.get<AnalyticsOverview>('/api/analytics/overview')
    return response.data
  },

  signalPerformance: async (days: number = 30) => {
    const response = await api.get<SignalPerformance[]>('/api/analytics/signals/performance', {
      params: { days },
    })
    return response.data
  },

  platformDistribution: async () => {
    const response = await api.get<PlatformDistribution[]>('/api/analytics/platform-distribution')
    return response.data
  },
}

// Community
export const communityAPI = {
  // Posts
  getFeed: async (params?: { feed_type?: 'following' | 'discover' | 'trending'; limit?: number; offset?: number }) => {
    const response = await api.get<Post[]>('/api/community/feed', { params })
    return response.data
  },

  createPost: async (data: { content: string; post_type?: string; images?: string[] }) => {
    const response = await api.post<Post>('/api/community/posts', data)
    return response.data
  },

  // Comments
  getComments: async (postId: number) => {
    const response = await api.get<Comment[]>(`/api/community/posts/${postId}/comments`)
    return response.data
  },

  addComment: async (postId: number, content: string, parentId?: number) => {
    const response = await api.post<Comment>(`/api/community/posts/${postId}/comments`, {
      content,
      parent_id: parentId,
    })
    return response.data
  },

  deleteComment: async (commentId: number) => {
    await api.delete(`/api/community/comments/${commentId}`)
  },

  // Reactions
  addReaction: async (postId: number, reactionType: string) => {
    await api.post(`/api/community/posts/${postId}/reactions`, null, {
      params: { reaction_type: reactionType.toUpperCase() },
    })
  },

  removeReaction: async (postId: number) => {
    await api.delete(`/api/community/posts/${postId}/reactions`)
  },

  // User Profile
  getProfile: async (userId: number) => {
    const response = await api.get<UserProfile>(`/api/community/profile/${userId}`)
    return response.data
  },

  updateProfile: async (data: Partial<UserProfile>) => {
    const response = await api.put<UserProfile>('/api/community/profile', data)
    return response.data
  },

  // Notifications
  getNotifications: async (unreadOnly: boolean = false) => {
    const response = await api.get<Notification[]>('/api/community/notifications', {
      params: { unread_only: unreadOnly },
    })
    return response.data
  },

  markNotificationAsRead: async (notificationId: number) => {
    await api.post(`/api/community/notifications/${notificationId}/read`)
  },

  markAllNotificationsAsRead: async () => {
    await api.post('/api/community/notifications/read-all')
  },

  getUnreadNotificationCount: async () => {
    const response = await api.get<{ count: number }>('/api/community/notifications/unread-count')
    return response.data.count
  },
}

// Public market data (no auth) — the dashboard ticker tape.
export const marketAPI = {
  // Cached server-side for ~60s, so polling this at the same interval costs
  // the exchange nothing beyond one batched call per minute.
  getTicker: async (): Promise<TickerTape> => {
    const response = await api.get('/api/market/ticker')
    return response.data
  },

  // 7-day close series per symbol, cached per symbol server-side for ~60s.
  getSparklines: async (symbols: string[]): Promise<SparklinesResponse> => {
    const response = await api.get('/api/market/sparklines', {
      params: { symbols: symbols.join(',') },
    })
    return response.data
  },

  // Close series for the live price chart, cached server-side per symbol and
  // timeframe (20s for 1m up to 5min for 1d).
  getCandles: async (symbol: string, timeframe: ChartTimeframe): Promise<CandleSeries> => {
    const response = await api.get('/api/market/candles', { params: { symbol, timeframe } })
    return response.data
  },
}

// In-app assistant: answers questions about FinCreator itself. Rate limited
// and capped per day server-side; a 429's `message` is written to be shown.
export const assistantAPI = {
  ask: async (payload: AssistantQuestion): Promise<AssistantAnswer> => {
    const response = await api.post('/api/assistant/ask', payload)
    return response.data
  },
}

// News Feeds
export const newsAPI = {
  /**
   * Headlines for the Dashboard news ticker: stored CoinDesk, Cointelegraph
   * and Bitcoin Magazine feed entries, newest first. A backend that predates
   * /api/news/headlines answers 404; /latest then serves the same feeds
   * (fetched live) and the ticker keeps only those outlets.
   */
  getHeadlines: async (limit: number = 30): Promise<NewsHeadline[]> => {
    try {
      const response = await api.get('/api/news/headlines', { params: { limit } })
      return toTickerHeadlines(response.data?.articles)
    } catch (error: any) {
      if (error?.response?.status !== 404) throw error
      const response = await api.get('/api/news/latest', { params: { limit: 60 } })
      return toTickerHeadlines(response.data?.articles).slice(0, limit)
    }
  },
}

// Monetization
export const monetizationAPI = {
  // Subscription Tiers
  getTiers: async (creatorId?: number) => {
    const endpoint = creatorId
      ? `/api/monetization/tiers/creator/${creatorId}`
      : '/api/monetization/tiers'
    const response = await api.get<SubscriptionTier[]>(endpoint)
    return response.data
  },

  // Subscriptions
  getSubscriptions: async (status?: string) => {
    const response = await api.get<Subscription[]>('/api/monetization/subscriptions/my', {
      params: { active_only: status ? status === 'active' : true }
    })
    return response.data
  },

  subscribe: async (
    tierId: number,
    paymentMethodId: string,
    isAnnual: boolean = false,
    discordUserId?: string,
    telegramUserId?: string,
    /** From a subscriber's share link; the server drops one that can't count. */
    referralCode?: string
  ) => {
    const response = await api.post<Subscription>('/api/monetization/subscriptions', {
      tier_id: tierId,
      is_annual: isAnnual,
      payment_method_id: paymentMethodId,
      discord_user_id: discordUserId || undefined,
      telegram_user_id: telegramUserId || undefined,
      referral_code: referralCode || undefined,
    })
    return response.data
  },

  cancelSubscription: async (subscriptionId: number) => {
    await api.post(`/api/monetization/subscriptions/${subscriptionId}/cancel`)
  },

  reactivateSubscription: async (subscriptionId: number) => {
    await api.post(`/api/monetization/subscriptions/${subscriptionId}/reactivate`)
  },

  // Tips
  // POST /api/monetization/tips charges the chosen saved card (Stripe) and
  // records the tip; the server refuses a request without one.
  sendTip: async (data: {
    creator_id: number
    amount_cents: number
    payment_method_id: string
    message?: string
    is_anonymous?: boolean
  }) => {
    const response = await api.post<Tip>('/api/monetization/tips', data)
    return response.data
  },

  // Payouts
  getPayouts: async () => {
    const response = await api.get<Payout[]>('/api/monetization/payouts')
    return response.data
  },

  // Payment Methods
  getPaymentMethods: async () => {
    const response = await api.get<PaymentMethod[]>('/api/monetization/payment-methods')
    return response.data
  },

  addPaymentMethod: async (data: any) => {
    const response = await api.post<PaymentMethod>('/api/monetization/payment-methods', data)
    return response.data
  },

  setDefaultPaymentMethod: async (methodId: string) => {
    await api.post(`/api/monetization/payment-methods/${methodId}/set-default`)
  },

  removePaymentMethod: async (methodId: string) => {
    await api.delete(`/api/monetization/payment-methods/${methodId}`)
  },
}

// Creator Profile (public-facing)
export const creatorProfileAPI = {
  getPublicProfile: async (creatorId: number) => {
    const response = await api.get<PublicProfileResponse>(`/api/creator-profile/${creatorId}`)
    return response.data
  },

  getProfileByDomain: async (domain: string) => {
    const response = await api.get<PublicProfileResponse>(`/api/creator-profile/by-domain/${domain}`)
    return response.data
  },

  getMyProfile: async () => {
    const response = await api.get<CreatorProfile>('/api/creator-profile/my-profile')
    return response.data
  },

  updateMyProfile: async (data: Partial<CreatorProfile>) => {
    const response = await api.patch<CreatorProfile>('/api/creator-profile/my-profile', data)
    return response.data
  },

  requestDomainVerificationToken: async () => {
    const response = await api.post<DomainVerificationToken>(
      '/api/creator-profile/my-profile/domain/verification-token'
    )
    return response.data
  },

  verifyDomain: async () => {
    const response = await api.post<DomainVerificationResult>(
      '/api/creator-profile/my-profile/domain/verify'
    )
    return response.data
  },
}

// Content Calendar
export const contentCalendarAPI = {
  schedule: async (data: {
    content_type: ScheduledContentType
    title: string
    body: string
    scheduled_at: string
    /** A library draft to publish as it stands, to its own network. */
    content_id?: number
    images?: string[]
    video_url?: string
    tags?: string[]
    mentions?: string[]
    timezone?: string
    notify_subscribers?: boolean
  }) => {
    const response = await api.post<ScheduledContent>('/api/content-calendar/schedule', data)
    return response.data
  },

  getScheduled: async (params?: {
    status_filter?: ScheduledContentStatus
    content_type?: ScheduledContentType
    skip?: number
    limit?: number
  }) => {
    const response = await api.get<ScheduledContent[]>('/api/content-calendar/scheduled', { params })
    return response.data
  },

  cancelScheduled: async (id: number) => {
    const response = await api.delete<ScheduledContent>(`/api/content-calendar/scheduled/${id}`)
    return response.data
  },
}

// Lock a call (LOCK_A_CALL_ENABLED; superusers only while it is on). Every
// route 404s while the feature is off.
export const callsAPI = {
  options: async () => {
    const response = await api.get<CallOptions>('/api/calls/options')
    return response.data
  },

  lock: async (data: LockCallRequest) => {
    const response = await api.post<LockedCall>('/api/calls/lock', data)
    return response.data
  },

  // The caller's locked calls, open first.
  mine: async () => {
    const response = await api.get<TradingSignal[]>('/api/calls/mine')
    return response.data
  },

  // What to paste into a TradingView alert so it locks a call.
  tradingviewSetup: async () => {
    const response = await api.get<TradingViewSetup>('/api/calls/tradingview/setup')
    return response.data
  },
}

// Public calls: no login. A 404 is an unknown id or one the public may not see.
export const publicCallsAPI = {
  get: async (id: number | string) => {
    const response = await api.get<PublicCall>(`/api/public/signals/${id}`)
    return response.data
  },

  // The /verify page: every published digest, newest first.
  digests: async (limit = 30, offset = 0) => {
    const response = await api.get<DigestListing>('/api/public/provenance/digests', { params: { limit, offset } })
    return response.data
  },

  // Recompute one call's fingerprints now and compare (same 404 rule as get).
  verify: async (id: number | string) => {
    const response = await api.get<CallCheck>(`/api/public/provenance/verify/${id}`)
    return response.data
  },
}

// The engine's publish rules, read-only, no login.
export interface EngineRules {
  min_stop_distance_pct: number
  /** From this time (UTC) stops and targets are scored at the level; null while that rule is off. Absent from older API builds. */
  level_exits_from?: string | null
  /** When a trained model (Phase 2) first decided what publishes; null while the fixed rule always has (a shadow model included). */
  phase2_model_from?: string | null
  /** When the fixed rule took the decision back; null while the model decides or never did. */
  phase2_rule_again_from?: string | null
}

export const engineRulesAPI = {
  get: async () => {
    const response = await api.get<EngineRules>('/api/public/engine-rules')
    return response.data
  },
  // Every period the engine was paused, newest first: when, who and why.
  pauses: async () => {
    const response = await api.get<EnginePause[]>('/api/public/engine-pauses')
    return response.data
  },
  // The engine's record split by the engine version that published it.
  versions: async () => {
    const response = await api.get<EngineVersionRecord[]>('/api/public/engine-versions')
    return response.data
  },
}

// The personal fee calculator: the engine's record netted at the fee entered
// (percent of entry per side, fee only). No login.
// A creator's public record (/c/<handle>): no login.
export const publicCreatorsAPI = {
  get: async (handle: string) => {
    const response = await api.get<CreatorRecord>(`/api/public/creators/${encodeURIComponent(handle)}`)
    return response.data
  },

  // One creator's closed calls netted at a fee: the fee calculator's own
  // backend calculation (net_record), keyed by the creator's user id.
  feeCalculator: async (creatorId: number, feePctPerSide: number) => {
    const response = await api.get<FeeCalculatorRecord>(`/api/public/creators/by-id/${creatorId}/fee-calculator`, {
      params: { fee_rate: feePctPerSide },
    })
    return response.data
  },
}

export const feeCalculatorAPI = {
  get: async (feePctPerSide: number) => {
    const response = await api.get<FeeCalculatorRecord>('/api/public/fee-calculator', {
      params: { fee_rate: feePctPerSide },
    })
    return response.data
  },
}

// Track Record
export const trackRecordAPI = {
  // Canonical signal numbers (closed, win rate, open split, heat, Phase 2
  // progress). Distinct from getSummary, which reports logged trade records.
  // demo=true scopes to demo signals; the default covers real signals only.
  // Closed signals excluded from the record: listed with the result they
  // recorded (unchanged) and why, but in none of the summary's figures.
  getExcluded: async () => {
    const response = await api.get<ExcludedSignal[]>('/api/track-record/excluded')
    return response.data
  },

  // When the engine's signals switched between the Phase 2 model and the
  // Phase 1 rule, oldest first; empty while the rule has always decided.
  getDeciderHistory: async () => {
    const response = await api.get<Phase2DeciderChange[]>('/api/track-record/decider-history')
    return response.data
  },

  // Gross: no fee is passed, so every net figure in the response is null.
  getSignalSummary: async (demo = false) => {
    const params: Record<string, unknown> = {}
    if (demo) params.demo = true
    const response = Object.keys(params).length
      ? await api.get<SignalTrackRecordSummary>('/api/track-record/summary', { params })
      : await api.get<SignalTrackRecordSummary>('/api/track-record/summary')
    return response.data
  },

  getSummary: async (period: PerformancePeriod = 'all_time', limit: number = 10) => {
    const response = await api.get<TrackRecordSummary>(`/api/track-record/summary/${period}`, {
      params: { limit },
    })
    return response.data
  },

  createTrade: async (data: {
    symbol: string
    direction: TradeDirection
    entry_price: number
    entry_time: string
    stop_loss?: number
    take_profit?: number
    timeframe?: string
    strategy_name?: string
    notes?: string
  }) => {
    const response = await api.post<TradeRecord>('/api/track-record/trades', data)
    return response.data
  },

  closeTrade: async (tradeId: number, data: { exit_price: number; exit_time: string }) => {
    const response = await api.patch<TradeRecord>(
      `/api/track-record/trades/${tradeId}/close`,
      data
    )
    return response.data
  },

  deleteTrade: async (tradeId: number) => {
    await api.delete(`/api/track-record/trades/${tradeId}`)
  },
}

// Signal Packages Marketplace
export const marketplaceAPI = {
  search: (filters = {}) => api.post('/api/signal-packages/marketplace/search', filters).then(r => r.data),
  purchase: (packageId: number, paymentMethodId: string) =>
    api.post(`/api/signal-packages/packages/${packageId}/purchase`, { payment_method_id: paymentMethodId }).then(r => r.data),
  getPurchased: () => api.get('/api/signal-packages/my-packages/purchased').then(r => r.data),
}

// Leaderboard
export const leaderboardAPI = {
  list: () => api.get('/api/community-hub/leaderboards').then(r => r.data),
  get: (id: number, limit = 20) =>
    api.get(`/api/community-hub/leaderboards/${id}`, { params: { limit } }).then(r => r.data),
}

// Price Alerts
export const priceAlertsAPI = {
  list: () => api.get('/api/price-alerts/').then(r => r.data),
  create: (data: any) => api.post('/api/price-alerts/', data).then(r => r.data),
  update: (id: number, data: any) => api.patch(`/api/price-alerts/${id}`, data).then(r => r.data),
  delete: (id: number) => api.delete(`/api/price-alerts/${id}`).then(r => r.data),

  // Fired alerts, for the notification bell
  getTriggers: async (unreadOnly: boolean = false) => {
    const response = await api.get<AlertTrigger[]>('/api/price-alerts/triggers', {
      params: { unread_only: unreadOnly },
    })
    return response.data
  },
  getUnreadTriggerCount: async () => {
    const response = await api.get<{ count: number }>('/api/price-alerts/triggers/unread-count')
    return response.data.count
  },
  markTriggerAsRead: async (triggerId: number) => {
    await api.post(`/api/price-alerts/triggers/${triggerId}/read`)
  },
  markAllTriggersAsRead: async () => {
    await api.post('/api/price-alerts/triggers/read-all')
  },
}

// Compliance / GDPR self-service
export const complianceAPI = {
  getDisclaimer: async (contentType: string, jurisdiction?: string) => {
    const response = await api.get<DisclaimerResponse>('/api/compliance/disclaimer', {
      params: { content_type: contentType, jurisdiction },
    })
    return response.data
  },

  exportMyData: async () => {
    const response = await api.get('/api/compliance/gdpr/export')
    return response.data
  },

  /** A creator's posts (with the posting check) and locked calls (with receipts) for a period. */
  creatorExport: async (params: { since?: string; until?: string } = {}) => {
    const response = await api.get('/api/compliance/creator-export', { params })
    return response.data
  },

  requestAccountDeletion: async (reason?: string) => {
    const response = await api.post<DataRequest>('/api/compliance/gdpr/delete-account', { reason })
    return response.data
  },

  getConsentStatus: async () => {
    const response = await api.get<Record<string, ConsentStatus>>('/api/compliance/gdpr/consent')
    return response.data
  },

  updateConsent: async (consentType: ConsentType, granted: boolean) => {
    const response = await api.post<ConsentRecord>('/api/compliance/gdpr/consent', {
      consent_type: consentType,
      granted,
    })
    return response.data
  },

  getMyVetting: async () => {
    const response = await api.get<CreatorVettingResponse>(
      '/api/compliance/creator-vetting/me'
    )
    return response.data
  },

  submitVettingApplication: async (submission: CreatorVettingSubmission) => {
    const response = await api.post<CreatorVettingResponse>(
      '/api/compliance/creator-vetting',
      submission
    )
    return response.data
  },

  listPendingVetting: async (limit = 50, offset = 0) => {
    const response = await api.get<CreatorVettingResponse[]>(
      '/api/compliance/creator-vetting',
      { params: { limit, offset } }
    )
    return response.data
  },

  reviewVetting: async (vettingId: number, review: CreatorVettingReview) => {
    const response = await api.post<CreatorVettingResponse>(
      `/api/compliance/creator-vetting/${vettingId}/review`,
      review
    )
    return response.data
  },
}

// A/B Testing Framework
export const abTestingAPI = {
  list: async (status?: ABTestStatus) => {
    const response = await api.get<ABTest[]>('/api/ab-testing/', {
      params: status ? { status } : undefined,
    })
    return response.data
  },

  create: async (data: ABTestCreateRequest) => {
    const response = await api.post<ABTest>('/api/ab-testing/', data)
    return response.data
  },

  start: async (testId: number) => {
    const response = await api.post<ABTest>(`/api/ab-testing/${testId}/start`)
    return response.data
  },

  cancel: async (testId: number) => {
    const response = await api.post<ABTest>(`/api/ab-testing/${testId}/cancel`)
    return response.data
  },

  getReport: async (testId: number) => {
    const response = await api.get<ABTestReport>(`/api/ab-testing/${testId}/report`)
    return response.data
  },
}

// Live Trading Room
export const liveRoomAPI = {
  create: async (data: LiveSessionCreateRequest) => {
    const response = await api.post<LiveSession>('/api/live-room/', data)
    return response.data
  },

  listOwn: async (skip: number = 0, limit: number = 50) => {
    const response = await api.get<LiveSession[]>('/api/live-room/', {
      params: { skip, limit },
    })
    return response.data
  },

  start: async (sessionId: number) => {
    const response = await api.post<LiveSession>(`/api/live-room/${sessionId}/start`)
    return response.data
  },

  end: async (sessionId: number) => {
    const response = await api.post<LiveSession>(`/api/live-room/${sessionId}/end`)
    return response.data
  },

  getJoinToken: async (sessionId: number) => {
    const response = await api.post<LiveJoinToken>(`/api/live-room/${sessionId}/join-token`)
    return response.data
  },

  getChatHistory: async (sessionId: number, limit: number = 100) => {
    const response = await api.get<LiveChatMessage[]>(
      `/api/live-room/${sessionId}/chat-history`,
      { params: { limit } }
    )
    return response.data
  },
}

export { api }
export default api

// Platform admin (superuser only). Never called from a subscriber-facing page.
// Tip goals (backend app/api/tip_goals.py). A contribution is charged like a
// tip, to a saved card.
/** A creator in discovery: their public record, before fees (backend api/discover.py). */
export interface DiscoverCreator {
  creator_id: number
  handle: string
  display_name: string
  bio: string | null
  subscriber_count: number
  min_tier_price_cents: number | null
  closed_calls: number
  open_calls: number
  first_call_at: string
  last_call_at: string
  too_few_to_judge: boolean
  min_closed_to_judge: number
  win_rate: number | null
  win_rate_low: number | null
  win_rate_high: number | null
  avg_r_gross: number | null
  record_path: string
  profile_path: string
}

export type DiscoverSort = 'calls' | 'recent' | 'win_rate_low'

export const discoverAPI = {
  creators: async (params: { sort_by: DiscoverSort; judged_only?: boolean }) =>
    (
      await api.get<{ total: number; sort_by: DiscoverSort; creators: DiscoverCreator[] }>('/api/discover/creators', {
        params,
      })
    ).data,
}

/** Subscriber referrals (backend api/referrals.py). */
export interface ReferralLink {
  creator_id: number
  referral_code: string
  /** /join/<code> */
  share_path: string
  /** The creator gives a free month for each subscriber the link brings in. */
  rewards_enabled: boolean
  conversions: { converted_at: string | null; reward_status: string | null; rewarded_at: string | null }[]
  months_earned: number
}

export interface CreatorReferralSummary {
  rewards_enabled: boolean
  subscribers_referred: number
  months_given: number
}

export const referralsAPI = {
  /** Your lasting share link for a creator you subscribe to. */
  link: async (creatorId: number) =>
    (await api.post<ReferralLink>('/api/referrals/', { creator_id: creatorId })).data,
  mine: async (creatorId: number) =>
    (await api.get<ReferralLink | null>('/api/referrals/mine', { params: { creator_id: creatorId } })).data,
  /** Public: who a share link is for. */
  track: async (code: string) =>
    (
      await api.get<{ referral_code: string; creator_id: number; creator_username: string; creator_display_name: string }>(
        `/api/referrals/track/${encodeURIComponent(code)}`
      )
    ).data,
  settings: async () => (await api.get<CreatorReferralSummary>('/api/referrals/settings')).data,
  setRewards: async (enabled: boolean) =>
    (await api.put<CreatorReferralSummary>('/api/referrals/settings', { rewards_enabled: enabled })).data,
}

export const pollsAPI = {
  /** A creator's polls, newest first; open ones only unless includeClosed. */
  list: async (creatorId: number, includeClosed = false) =>
    (await api.get<Poll[]>('/api/polls/', { params: { creator_id: creatorId, include_closed: includeClosed } })).data,
  create: async (poll: PollCreate) => (await api.post<Poll>('/api/polls/', poll)).data,
  vote: async (pollId: number, optionIndex: number) =>
    (await api.post<Poll>(`/api/polls/${pollId}/vote`, { option_index: optionIndex })).data,
  close: async (pollId: number) => {
    await api.delete(`/api/polls/${pollId}`)
  },
}

export const tipGoalsAPI = {
  list: async (creatorId: number, includeCompleted = false) => {
    const response = await api.get<TipGoal[]>('/api/tip-goals/', {
      params: { creator_id: creatorId, include_completed: includeCompleted },
    })
    return response.data
  },

  create: async (goal: TipGoalCreate) => {
    const response = await api.post<TipGoal>('/api/tip-goals/', goal)
    return response.data
  },

  contribute: async (goalId: number, data: { amount_cents: number; payment_method_id: string; message?: string }) => {
    const response = await api.post<TipGoal>(`/api/tip-goals/${goalId}/contribute`, data)
    return response.data
  },

  end: async (goalId: number) => {
    await api.delete(`/api/tip-goals/${goalId}`)
  },
}

/** The subset of GET/PATCH /api/notifications/preferences the app edits. */
export interface EmailPreferences {
  notifications_enabled: boolean
  email_enabled: boolean
  email_subscription_updates: boolean
}

/** Developer settings: API keys and each key's /api/v1 webhooks (backend api/api_keys.py). */
export interface DeveloperKey {
  id: number
  name: string
  key_prefix: string
  scopes: string
  is_active: boolean
  last_used_at: string | null
  expires_at: string | null
  created_at: string
}

export type WebhookEvent = 'signal.created' | 'signal.closed' | 'content.published'

export interface DeveloperWebhook {
  id: number
  creator_id: number
  creator_handle: string | null
  target_url: string
  events: WebhookEvent[]
  is_active: boolean
  created_at: string
}

export const developerAPI = {
  keys: async () => (await api.get<DeveloperKey[]>('/api/api-keys')).data,
  /** The raw key is in this response only. */
  createKey: async (data: { name: string; expires_in_days: number | null; scopes?: string }) =>
    (await api.post<DeveloperKey & { raw_key: string }>('/api/api-keys', data)).data,
  revokeKey: async (id: number) => {
    await api.delete(`/api/api-keys/${id}`)
  },
  webhooks: async (keyId: number) =>
    (await api.get<DeveloperWebhook[]>(`/api/api-keys/${keyId}/webhooks`)).data,
  /** The signing secret is in this response only. `creator` is a handle; empty = your own account. */
  createWebhook: async (keyId: number, data: { target_url: string; events: WebhookEvent[]; creator?: string }) =>
    (await api.post<DeveloperWebhook & { secret: string }>(`/api/api-keys/${keyId}/webhooks`, data)).data,
  deleteWebhook: async (keyId: number, webhookId: number) => {
    await api.delete(`/api/api-keys/${keyId}/webhooks/${webhookId}`)
  },
}

export const notificationsAPI = {
  getPreferences: async () => {
    const response = await api.get<EmailPreferences>('/api/notifications/preferences')
    return response.data
  },

  updatePreferences: async (changes: Partial<EmailPreferences>) => {
    const response = await api.patch<EmailPreferences>('/api/notifications/preferences', changes)
    return response.data
  },
}

export const adminAPI = {
  /**
   * Read-only replay of every closed real signal under alternative exit
   * rules. Fetches the candle path from the engine's venue, so it can take
   * several seconds; writes nothing.
   */
  exitBacktest: async (atrMultiple = 3) => {
    const response = await api.get<ExitBacktestReport>('/api/admin/exit-backtest', {
      params: { atr_multiple: atrMultiple },
    })
    return response.data
  },
  /**
   * Rejected setups and what they would have done. DB and Redis reads only:
   * the live-signal replay is computed by the hourly resolver, not here.
   */
  /** Phase 2 model vs the Phase 1 rule since the first model (Settings → Engine). */
  phase2Comparison: async () => {
    const response = await api.get<Phase2Comparison>('/api/admin/phase2-comparison')
    return response.data
  },
  /** The Phase 2 shadow trial scoreboard (Settings → Engine, top of Phase 2 vs Phase 1). */
  phase2Trial: async () => {
    const response = await api.get<Phase2Trial>('/api/admin/phase2-trial')
    return response.data
  },
  /** Two candidate rules, recorded and never applied (Settings → Engine). */
  shadowFilters: async () => {
    const response = await api.get<ShadowFiltersReport>('/api/admin/shadow-filters')
    return response.data
  },
  shadowOutcomes: async (days: 7 | 30 = 7) => {
    const response = await api.get<ShadowOutcomesReport>('/api/admin/diagnostics/shadow-outcomes', {
      params: { days },
    })
    return response.data
  },
  /** What the platform can do right now, from config and recorded failures. */
  capabilities: async () => {
    const response = await api.get<CapabilityReport>('/api/admin/diagnostics/capabilities')
    return response.data
  },
  /** Closed rows whose stored outcome or P&L disagrees with their prices. Read-only. */
  outcomeAudit: async () => {
    const response = await api.get<OutcomeAuditReport>('/api/admin/diagnostics/outcome-audit')
    return response.data
  },

  enginePause: async () => {
    const response = await api.get<EnginePauseReport>('/api/admin/diagnostics/engine-pause')
    return response.data
  },

  setEnginePause: async (paused: boolean, reason: string) => {
    const response = await api.post<EnginePauseReport>('/api/admin/diagnostics/engine-pause', { paused, reason })
    return response.data
  },
  /** Each beat task's last runs and whether it is late or failing. */
  scheduledTasks: async () => {
    const response = await api.get<ScheduledTasksReport>('/api/admin/diagnostics/scheduled-tasks')
    return response.data
  },
  /** The engine diagnostics panel's facts. DB and Redis reads only; no AI. */
  diagnosticsFacts: async () => {
    const response = await api.get<DiagnosticsFacts>('/api/admin/diagnostics/facts')
    return response.data
  },
  /**
   * One diagnosis turn. May call a paid model (capped per day server-side);
   * trade and market questions are declined without one. Send only a
   * question and, for a follow-up, the conversation id -- the server keeps
   * the history and rejects any other field.
   */
  diagnose: async (body: { question?: string; conversation_id?: string }) => {
    const response = await api.post<DiagnosisTurn>('/api/admin/diagnostics/diagnose', body)
    return response.data
  },
  /** The admin's last 10 diagnosis conversations and today's calls left. */
  diagnosisConversations: async () => {
    const response = await api.get<DiagnosisConversations>('/api/admin/diagnostics/conversations')
    return response.data
  },
  /** One past diagnosis conversation, turn by turn, as it was shown. Read-only. */
  diagnosisConversation: async (conversationId: string) => {
    const response = await api.get<DiagnosisConversationDetail>(
      `/api/admin/diagnostics/conversations/${conversationId}`,
    )
    return response.data
  },
  /** Engine fact alerts (no AI), newest first, read or unread for this admin. */
  engineEvents: async (params: { unread?: boolean; limit?: number } = {}) => {
    const response = await api.get<EngineEvents>('/api/admin/diagnostics/events', { params })
    return response.data
  },
  markEngineEventSeen: async (eventId: number, how: 'opened' | 'dismissed' = 'opened') => {
    const response = await api.post(`/api/admin/diagnostics/events/${eventId}/seen`, { how })
    return response.data
  },
  markAllEngineEventsSeen: async () => {
    const response = await api.post<{ marked: number }>('/api/admin/diagnostics/events/seen-all')
    return response.data
  },
}

// Courses: free or included with subscription tiers (/api/learning)
export type CourseFields = Partial<
  Pick<Course, 'title' | 'subtitle' | 'description' | 'level' | 'learning_objectives' | 'prerequisites' | 'included_tier_ids' | 'is_free' | 'price'>
>
export type LessonFields = Partial<
  Pick<Lesson, 'title' | 'description' | 'lesson_type' | 'content' | 'video_url' | 'is_preview' | 'is_published' | 'display_order' | 'duration_minutes'>
>

export const coursesAPI = {
  /** An instructor's courses: all of them for the instructor, the published ones for anyone else. */
  byInstructor: async (instructorId: number) =>
    (await api.get<Course[]>(`/api/learning/courses/instructor/${instructorId}`)).data,
  /** Published courses, most students first; search matches title and description. */
  search: async (params: { search?: string; level?: Course['level']; limit?: number } = {}) =>
    (await api.get<Course[]>('/api/learning/courses', { params })).data,
  bySlug: async (slug: string) => (await api.get<Course>(`/api/learning/courses/slug/${encodeURIComponent(slug)}`)).data,
  get: async (id: number) => (await api.get<Course>(`/api/learning/courses/${id}`)).data,
  create: async (fields: CourseFields & { title: string; slug: string; description: string; level: Course['level'] }) =>
    (await api.post<Course>('/api/learning/courses', fields)).data,
  update: async (id: number, fields: CourseFields) => (await api.put<Course>(`/api/learning/courses/${id}`, fields)).data,
  /** 422 with { detail: { message, problems } } when the posting check finds something (see courseProblems). */
  publish: async (id: number) => (await api.post<Course>(`/api/learning/courses/${id}/publish`)).data,

  modules: async (courseId: number) => (await api.get<CourseModule[]>(`/api/learning/courses/${courseId}/modules`)).data,
  addModule: async (courseId: number, fields: { title: string; description?: string; display_order?: number }) =>
    (await api.post<CourseModule>(`/api/learning/courses/${courseId}/modules`, fields)).data,
  updateModule: async (id: number, fields: Partial<Pick<CourseModule, 'title' | 'description' | 'display_order' | 'is_published'>>) =>
    (await api.put<CourseModule>(`/api/learning/modules/${id}`, fields)).data,
  deleteModule: async (id: number) => {
    await api.delete(`/api/learning/modules/${id}`)
  },

  lessons: async (moduleId: number) => (await api.get<Lesson[]>(`/api/learning/modules/${moduleId}/lessons`)).data,
  addLesson: async (moduleId: number, fields: LessonFields & { title: string; lesson_type: Lesson['lesson_type'] }) =>
    (await api.post<Lesson>(`/api/learning/modules/${moduleId}/lessons`, fields)).data,
  updateLesson: async (id: number, fields: LessonFields) => (await api.put<Lesson>(`/api/learning/lessons/${id}`, fields)).data,
  deleteLesson: async (id: number) => {
    await api.delete(`/api/learning/lessons/${id}`)
  },

  enroll: async (courseId: number) =>
    (await api.post<CourseEnrollment>('/api/learning/enrollments', { course_id: courseId })).data,
  /** Whether a course can be sold on its own, and the price bounds (USD). */
  salesStatus: async () =>
    (await api.get<{ enabled: boolean; min_price: number; max_price: number }>('/api/learning/course-sales')).data,
  /** Buy a paid course with a saved card; 3-D Secure goes through completeCardPayment. */
  purchase: async (courseId: number, paymentMethodId: string) =>
    (
      await api.post<{
        payment_id: number
        course_id: number
        amount_cents: number
        currency: string
        payment_status: string | null
        client_secret: string | null
        enrolled: boolean
      }>(`/api/learning/courses/${courseId}/purchase`, { payment_method_id: paymentMethodId })
    ).data,
  myEnrollments: async () => (await api.get<CourseEnrollment[]>('/api/learning/enrollments')).data,
  progress: async (enrollmentId: number) =>
    (await api.get<LessonProgress[]>(`/api/learning/enrollments/${enrollmentId}/progress`)).data,
  complete: async (enrollmentId: number, lessonId: number) =>
    (
      await api.put<LessonProgress>(`/api/learning/enrollments/${enrollmentId}/lessons/${lessonId}/progress`, {
        completion_percentage: 100,
        is_completed: true,
      })
    ).data,

  /** The lesson tutor: answered from the lesson itself. */
  ask: async (lessonId: number, question: string) =>
    (await api.post<LessonQuestion>(`/api/learning/lessons/${lessonId}/ask`, { question })).data,
  myQuestions: async (lessonId: number) =>
    (await api.get<LessonQuestion[]>(`/api/learning/lessons/${lessonId}/questions`)).data,
  // Quizzes are lessons; passing one marks it done.
  addQuizLesson: async (moduleId: number, fields: { title: string; passing_score?: number; max_attempts?: number }) =>
    (await api.post<Lesson>(`/api/learning/modules/${moduleId}/quiz-lessons`, fields)).data,
  quiz: async (quizId: number) => (await api.get<QuizView>(`/api/learning/quizzes/${quizId}`)).data,
  updateQuiz: async (quizId: number, fields: { title?: string; passing_score?: number; max_attempts?: number }) =>
    (await api.put<QuizView>(`/api/learning/quizzes/${quizId}`, fields)).data,
  addQuizQuestion: async (
    quizId: number,
    fields: { question_text: string; options: string[]; correct_option: number; explanation?: string | null }
  ) => (await api.post<QuizQuestion>(`/api/learning/quizzes/${quizId}/questions`, fields)).data,
  deleteQuizQuestion: async (questionId: number) => {
    await api.delete(`/api/learning/quiz-questions/${questionId}`)
  },
  submitQuiz: async (quizId: number, answers: Record<number, number>) =>
    (await api.post<QuizResult>(`/api/learning/quizzes/${quizId}/submit`, { answers })).data,

  myCertificates: async () => (await api.get<CourseCertificate[]>('/api/learning/certificates')).data,
  verifyCertificate: async (number: string) =>
    (await api.get<CertificateCheck>(`/api/learning/certificates/verify/${encodeURIComponent(number)}`)).data,

  reviews: async (courseId: number) => (await api.get<CourseReviewsPage>(`/api/learning/courses/${courseId}/reviews`)).data,
  addReview: async (courseId: number, fields: { rating: number; title?: string; review_text?: string }) =>
    (await api.post<CourseReview>(`/api/learning/courses/${courseId}/reviews`, fields)).data,
  deleteReview: async (reviewId: number) => {
    await api.delete(`/api/learning/reviews/${reviewId}`)
  },

  discussions: async (courseId: number) =>
    (await api.get<CourseDiscussion[]>(`/api/learning/courses/${courseId}/discussions`)).data,
  startDiscussion: async (courseId: number, fields: { title: string; content: string }) =>
    (await api.post<{ id: number }>(`/api/learning/courses/${courseId}/discussions`, fields)).data,
  reply: async (discussionId: number, content: string) =>
    (await api.post<{ id: number }>(`/api/learning/discussions/${discussionId}/replies`, { content })).data,
  changeDiscussion: async (discussionId: number, fields: { is_locked?: boolean; is_pinned?: boolean }) => {
    await api.put(`/api/learning/discussions/${discussionId}`, fields)
  },
  removeDiscussion: async (discussionId: number) => {
    await api.delete(`/api/learning/discussions/${discussionId}`)
  },
  removeReply: async (replyId: number) => {
    await api.delete(`/api/learning/discussion-replies/${replyId}`)
  },

  /** How many students finished each lesson (the instructor's view: counts, never names). */
  lessonStats: async (courseId: number) =>
    (await api.get<{ students: number; lessons: { lesson_id: number; completed: number }[] }>(
      `/api/learning/courses/${courseId}/lesson-stats`
    )).data,
  /** Report a review, thread or reply for an admin to look at. */
  report: async (item_type: 'review' | 'discussion' | 'reply', item_id: number, reason: CourseReportReason, note?: string) => {
    await api.post('/api/learning/reports', { item_type, item_id, reason, note })
  },
  /** Admins: open reports, one row per item. */
  reports: async () => (await api.get<CourseReportItem[]>('/api/learning/reports')).data,
  decideReport: async (reportId: number, action: 'remove' | 'keep') => {
    await api.post(`/api/learning/reports/${reportId}/decide`, { action })
  },
  /** What students asked the tutor in a course (the instructor's view: not who asked). */
  courseQuestions: async (courseId: number) =>
    (await api.get<CourseQuestion[]>(`/api/learning/courses/${courseId}/questions`)).data,
}
